import { useState } from "react";
import { useApp } from "../context/AppContext";

const HELP_SECTIONS = [
  // ── ГОЛОВНЕ ──
  { id: "_head_main", divider: "Головне" },
  {
    id: "dashboard", icon: "📊", title: "Дашборд",
    desc: "Головна панель проекту з усіма ключовими метриками.",
    details: [
      "Кліки, покази, позиції з Google Search Console",
      "AI Видимість — відсоток цитування сайту в AI-пошуку",
      "Графік трафіку за 90 днів",
      "Розподіл позицій (TOP-3, TOP-10, TOP-30)",
      "Беклінки та реферальні домени",
      "Індексація сторінок",
    ],
    howTo: "Дані кешуються на 1 годину. Для оновлення — перезайдіть в проект.",
  },
  {
    id: "onboarding", icon: "✅", title: "Онбординг",
    desc: "Чек-лист для нового проекту — автоматична перевірка 25+ пунктів.",
    details: [
      "Доступи — GSC, Analytics, CMS, хостинг",
      "Технічний аудит — SSL, sitemap, robots, canonical, hreflang, Schema.org, TTFB, security headers",
      "Піддомени — автоматичний пошук через Certificate Transparency",
      "PageSpeed — автоматична перевірка швидкості",
    ],
    howTo: "Натисніть 'Авто-перевірка' — система перевірить все автоматично.",
  },
  {
    id: "positions", icon: "📈", title: "Перевірка позицій",
    desc: "Зняття реальних позицій по всіх ключових словах через Google SERP.",
    details: [
      "Перевірка через Serper API (реальна видача Google)",
      "Перевіряє до 5 сторінок Google на кожен ключ",
      "Абсолютна позиція (не відносна)",
      "Порівняння з попереднім знімком — зміни позицій",
      "Історія знімків — вибір дати для порівняння",
    ],
    howTo: "Перевірка позицій → натисніть 'Перевірити'. Потрібна завантажена семантика. Перевірка всіх ключів ~2-5 хв.",
  },

  // ── СЕМАНТИКА ──
  { id: "_head_sem", divider: "Семантика" },
  {
    id: "semantics", icon: "📊", title: "Семантичне ядро (Імпорт + Перегляд)",
    desc: "Об'єднаний модуль: імпорт ключів з CSV та перегляд кластерів з аналізом.",
    details: [
      "Таб 'Перегляд' — кластери по URL, фільтрація, сортування, пагінація",
      "Таб 'Імпорт CSV' — завантаження CSV/TSV, автоматична кластеризація",
      "Режим 'Доповнити' — додає нові ключі до існуючих кластерів без втрати даних",
      "Упущені ключі — порівняння з GSC та Serpstat",
      "AI Brief — аналіз сторінки проти ТОП-10 конкурентів",
      "AEO: перші 200 слів = зона витягування для AI, answer capsule в описі",
    ],
    howTo: "Імпорт CSV → завантажте файл. Перегляд → оберіть URL → AI Brief для рекомендацій.",
  },
  {
    id: "semantic_cleaner", icon: "🧹", title: "Збір семантики",
    desc: "Автоматичний збір ключових слів з GSC та Serpstat по кожній сторінці.",
    details: [
      "Посторінковий збір: для кожного URL зі структури",
      "Джерела: GSC (реальні запити), Serpstat (getUrlKeywords, getRelatedKeywords)",
      "Скоринг: відповідність назві сторінки, стемінг, GSC boost",
      "Класифікація: keep (>=3), review (>=0), reject (<0)",
      "Затвердження — ключі зливаються в кластери",
    ],
    howTo: "Збір семантики → натисніть 'Зібрати'. Фоновий процес, можна закрити вкладку.",
  },
  {
    id: "meta", icon: "🏷️", title: "Meta-теги",
    desc: "AI генерація Title, Description, H1 з AEO-оптимізацією.",
    details: [
      "Автоматична генерація через Claude AI",
      "Title: мінімум 100 символів, 3+ ключі, ВЧ-ключ обов'язково",
      "Description = Answer Capsule — пряма відповідь для AI-цитування",
      "Description містить факт/число + ключі + CTA",
      "Пакетна генерація з фоновою обробкою",
    ],
    howTo: "Потрібна семантика. Meta-теги → 'Згенерувати'. Результати зберігаються в кластерах.",
  },
  {
    id: "meta_formula", icon: "📐", title: "ТЗ Мета-формули",
    desc: "Шаблони генерації мета-тегів з динамічними змінними.",
    details: [
      "6 пресетів: товар, каталог, фільтри, блог, послуга, загальний",
      "Змінні: {H1}, {city}, {brand}, {year} та інші",
      "Кастомні змінні з списком значень",
      "Попередній перегляд з підстановкою",
    ],
    howTo: "Оберіть пресет або створіть свій шаблон. Змінні підставляться автоматично.",
  },
  {
    id: "content", icon: "📝", title: "Контент",
    desc: "AI генерація текстів з AEO-оптимізацією для AI-пошуку.",
    details: [
      "Типи сторінок: каталог, товар, фільтрова, послуга, блог",
      "AEO: перші 200 слів = зона витягування, речення <17 слів",
      "Щільність фактів: 1 факт на 80 слів",
      "Пакетна генерація з прогресом",
    ],
    howTo: "Оберіть тип сторінки → натисніть 'Згенерувати'.",
  },

  // ── SEO ІНСТРУМЕНТИ ──
  { id: "_head_seo", divider: "SEO Інструменти" },
  {
    id: "structure", icon: "🌳", title: "Структура сайту",
    desc: "Візуальний редактор дерева структури з аналізом глибини.",
    details: [
      "Дерево сторінок з вкладеністю",
      "Аналіз глибини — скільки кліків від головної",
      "AI рекомендації по розширенню",
      "Drag & drop редагування",
    ],
    howTo: "Додайте URL вручну або імпортуйте з sitemap.",
  },
  {
    id: "seo_cocoon", icon: "🕸️", title: "SEO Кокон — як формується (перелінковка + SEO-текст)",
    desc: "Автоматична СЕМАНТИЧНА перелінковка. Для кожної сторінки будується блок (Формат C): зліва — релевантні внутрішні посилання, справа — H2-питання + SEO-текст з посиланнями. Усе формується з СЕМАНТИКИ (кластерів і ключів), а не випадково.",
    details: [
      "📥 ДЖЕРЕЛО ДАНИХ — семантичне ядро: кластери (URL + список ключів + частоти hf_freq). Жодного краулінгу сайту — лише ваша семантика.",
      "КРОК 1 · Кластеризація + ролі: сторінки групуються в кластери за перетином ключів; усередині кластера призначаються ролі main / support / sub-support (головна / підтримуюча / під-підтримуюча).",
      "КРОК 2 · Блок «Підкатегорії» (зліва, чипи): релевантні сторінки. Спершу — зі свого кластера за ролями (main→support і т.д.). Якщо мало — добір за СЛОВНИМ перетином ключів (напр. «крем для обличчя» ↔ «крем для рук» — спільне слово «крем»), далі — за вагою сторінки. Анкор чипа = ключ цільової сторінки. Працює і для пласких магазинів (де всі сторінки 'main').",
      "КРОК 3 · Блок «Тематичні запити» (зліва, фіолетові чипи): N-грами з СЕМАНТИКИ — біграми/триграми зі слів ключів кластерів (напр. «укладання волосся», «крем обличчя», «сонцезахисний крем»). Кожна n-грама веде на сторінку, чий ключ її сформував (owner-URL). Шум (валюта/ціни/цифри/«купити»/«знято») відфільтровано.",
      "КРОК 4 · SEO-текст (правий тёмний блок): H2-питання з головного ВЧ-ключа сторінки (формат «Що таке …?») + абзац-відповідь, згенерований AI українською, де ті самі n-грами органічно вплетені як посилання-анкори на релевантні сторінки. Генерується для кожної сторінки і кешується.",
      "КРОК 5 · Кокон на ВСЬОМУ сайті: для сторінок, яких немає в коконі (нові товари тощо) показується дефолтний блок — топ-популярні розділи. Тобто блок є на кожній сторінці, а не лише на тих, що в Spalah.",
      "⚙️ ВСТАВКА на сайт: SDK рендерить блок у <div id='spalah-seo'> зі своїми ізольованими стилями (.spalah-kokon-c) — не конфліктують зі стилями сайту. На вузьких екранах блок схлопується в одну колонку.",
      "🏷️ Підпис «powered by Spalah · Svitlo agency product» — вимкнено за замовчуванням, вмикається тумблером у налаштуваннях SDK.",
      "🎯 НАВІЩО: рівномірно розподіляє внутрішню вагу між сторінками, підсилює тематичну релевантність кластерів і дає AI-двигунам (AEO) чітку структуру «питання → відповідь» з релевантними посиланнями.",
    ],
    howTo: "Потрібна готова семантика (кластери з ключами). Модуль 'SEO Кокон' → таб 'Реалізація' → 'Згенерувати'. Чипи-посилання рахуються миттєво, SEO-тексти догенеровуються у фоні (~1-2 хв). Потім увімкніть тумблер 'SEO Кокон' у налаштуваннях SDK — блок з'явиться на сайті.",
  },
  {
    id: "seo_editor", icon: "✏️", title: "SEO Редактор",
    desc: "WYSIWYG редактор з live SEO-оцінкою та перевіркою унікальності.",
    details: [
      "Rich-text редактор",
      "SEO Score в реальному часі",
      "N-грам підказки з семантики",
      "Внутрішня унікальність (5-gram Jaccard)",
      "Зовнішня унікальність (Google search)",
    ],
    howTo: "Оберіть сторінку → вставте текст → SEO Score оновлюється автоматично.",
  },
  {
    id: "gsc_analysis", icon: "📈", title: "Аналіз GSC (Точки росту + Канібалізація)",
    desc: "Два інструменти на базі GSC даних в одному модулі.",
    details: [
      "Таб 'Точки росту' — ключі на позиціях 4-20 з високими показами",
      "Нові ключі яких немає в семантиці",
      "Рекомендації по кожному ключу",
      "Таб 'Канібалізація' — ключі де 2+ сторінки конкурують між собою",
      "AI резолюція конфлікту: об'єднати, canonical, noindex, розділити",
    ],
    howTo: "Потрібне підключення GSC. Аналіз автоматичний при відкритті.",
  },
  {
    id: "ngrams", icon: "📊", title: "N-грам аналіз",
    desc: "Частотний аналіз словосполучень у ключових словах.",
    details: [
      "Біграми, триграми, тетраграми",
      "Автоматична фільтрація стоп-слів",
      "Теплова карта частотності",
      "Експорт CSV/TSV",
    ],
    howTo: "Потрібна семантика. Відкрийте модуль — аналіз автоматичний.",
  },
  {
    id: "wikilink", icon: "🔗", title: "Вікі-лінковка",
    desc: "Пошук можливостей для внутрішніх посилань на основі ключів.",
    details: [
      "Вставте текст → система знайде де згадуються теми інших сторінок",
      "Рекомендує анкори та URL для лінків",
      "Працює на основі ключів з кластерів",
    ],
    howTo: "Вставте текст → автоматичний пошук релевантних лінків.",
  },

  // ── ПОСИЛАННЯ ──
  { id: "_head_links", divider: "Посилання" },
  {
    id: "linkgen", icon: "🔗", title: "Статті для посилань",
    desc: "AI генерація гостьових статей з вшитими посиланнями.",
    details: [
      "Тема, анкор, URL, кількість символів",
      "Підтримка 2 посилань на статтю",
      "Мови: українська, російська, англійська",
      "Anchor bank — збережені анкори",
    ],
    howTo: "Заповніть поля → 'Згенерувати'. Скопіюйте та розмістіть на донорі.",
  },

  // ── МОНІТОРИНГ ──
  { id: "_head_mon", divider: "Моніторинг" },
  {
    id: "pagespeed", icon: "⚡", title: "PageSpeed",
    desc: "Google PageSpeed Insights — перевірка швидкості сайту.",
    details: [
      "Перевірка Mobile + Desktop одночасно",
      "Core Web Vitals: FCP, LCP, CLS, TBT, SI",
      "Пакетна перевірка до 30 репрезентативних URL",
      "Середні оцінки по всіх сторінках",
      "Кнопка 'Стоп + Очистити' для зупинки і видалення",
    ],
    howTo: "Введіть URL → 'Перевірити'. Або натисніть 'Всі' для масової перевірки.",
  },
  {
    id: "ai_visibility", icon: "🤖", title: "AI Видимість",
    desc: "Чи цитується ваш сайт в AI-пошуку (ChatGPT, Perplexity, Gemini).",
    details: [
      "Перевірка ТОП ключів через Tavily AI Search",
      "Відсоток цитування: скільки запитів повертають ваш сайт",
      "Конкуренти в AI — хто цитується замість вас",
      "Історія по тижнях",
    ],
    howTo: "AI Видимість → 'Перевірити'. Результат за ~1 хв.",
  },
  {
    id: "site_scan", icon: "🔎", title: "Сканер сайту",
    desc: "Парсинг Title/H1/Status всіх сторінок з алертами змін.",
    details: [
      "Паралельний краулінг (5 одночасно)",
      "Відстеження змін: Title, H1, Status Code",
      "Внутрішні посилання — збір для SEO Кокону",
      "Автоматичне сканування кожні 6 годин (cron)",
    ],
    howTo: "Потрібна семантика. Сканер → 'Сканувати'. Результати використовуються в SEO Кокон і Вікі-лінковці.",
  },
  {
    id: "firehose", icon: "🔥", title: "Firehose",
    desc: "Моніторинг змін контенту на сайті через Firehose.com.",
    details: [
      "Автоматичне відстеження змін: title, контент, посилання",
      "Diff візуалізація: зелений = додано, червоний = видалено",
      "Telegram сповіщення при змінах",
      "Кожні 5 хв перевірка нових подій",
      "Ліміт: 25 правил на організацію",
    ],
    howTo: "Firehose → 'Підключити'. Потрібен API ключ (fhm_...).",
  },
  {
    id: "gsc", icon: "👁️", title: "GSC",
    desc: "Google Search Console — підключення та перегляд даних.",
    details: [
      "OAuth2 авторизація",
      "Ключові слова: кліки, покази, CTR, позиція",
      "Фільтрація по показам",
      "CSV вставка для імпорту",
    ],
    howTo: "GSC → 'Підключити'. Оберіть сайт → дані завантажаться автоматично.",
  },

  // ── СЕРВІСНІ ──
  { id: "_head_srv", divider: "Сервісні" },
  {
    id: "schema", icon: "📝", title: "Розмітка для сторінок блогу",
    desc: "Готова розмітка блогу: сторінка автора + шаблон статті.",
    details: [
      "Сторінка автора — ProfilePage / Person, публікація на author.house",
      "Шаблон статті — BlogPosting + FAQPage + BreadcrumbList",
      "Markdown-версія статті для AI-видачі (ChatGPT, AI Overviews)",
      "Автор прив'язаний через @id · місця для розробника {{помічені}}",
    ],
    howTo: "Розмітка блогу → оберіть «Сторінка автора» або «Шаблон статті», налаштуйте й скопіюйте.",
  },
  {
    id: "aeo", icon: "🤖", title: "AEO Hub — AI Engine Optimization",
    desc: "Комплексна оптимізація сайту для AI-пошуку (ChatGPT, Claude, Perplexity, Gemini).",
    details: [
      "llms.txt — навігаційний файл для LLM (аналог robots.txt для AI)",
      "llms-full.txt — повний контент-дамп сайту (до 50 сторінок, 2000 слів кожна)",
      "robots.txt AI — оптимізований robots: блокує training-ботів, дозволяє search-ботів",
      "IndexNow — миттєве сповіщення Bing та AI про зміни на сайті",
      "FAQ/Answer Capsules — FAQ з відповідями 40-60 слів + FAQPage Schema.org",
      "Боти: GPTBot, ClaudeBot (training) заблоковані; OAI-SearchBot, PerplexityBot (search) дозволені",
      "Answer Capsule = пряма відповідь для AI витягування (72% цитувань мають таку)",
    ],
    howTo: "AEO Hub → оберіть таб → 'Згенерувати'. Файли завантажте та розмістіть у кореневій директорії сайту. IndexNow потребує розміщення файлу ключа.",
  },
  {
    id: "sdk", icon: "📦", title: "Spalah SDK",
    desc: "JS-віджет для автоматичного впровадження SEO на сайт клієнта.",
    details: [
      "Автоматичне встановлення мета-тегів, H1, Schema.org",
      "SEO Кокон — віджет перелінковки",
      "BreadcrumbList автоматично",
      "localStorage fallback якщо Spalah недоступний",
      "Налаштування по модулях: meta, h1, cocoon, schema",
    ],
    howTo: "SDK → створіть токен → скопіюйте скрипт → вставте перед </body>.",
  },
  {
    id: "tickets", icon: "🎫", title: "Тікети (Проектні + Мої)",
    desc: "Два режими в одному модулі: тікети проекту та персональні.",
    details: [
      "Таб 'Проектні' — тікети прив'язані до поточного проекту",
      "Таб 'Мої тікети' — всі тікети призначені вам або створені вами",
      "Створення з пріоритетом (Low/Medium/High/Urgent)",
      "Коментарі та обговорення",
      "Telegram-сповіщення при новому тікеті",
    ],
    howTo: "Тікети → '+ Створити'. Виконавець отримає Telegram-повідомлення.",
  },
  {
    id: "report", icon: "📄", title: "Звіт для клієнта",
    desc: "Захищений паролем звіт з усіма метриками проекту.",
    details: [
      "Пароль для доступу",
      "GSC графіки, позиції, беклінки",
      "AI Видимість, SEO Кокон 3D",
      "План робіт з прогресом",
      "Тікети — клієнт може створювати запити",
      "Dark/Light тема",
    ],
    howTo: "Звіт → створіть посилання з паролем → надішліть URL клієнту.",
  },
];

export default function HelpView() {
  const { dark, t, card } = useApp();
  const [openId, setOpenId] = useState(null);

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>Довідка Spalah</h2>
        <p style={{ fontSize: 13, color: t.textMuted, marginTop: 6 }}>Опис всіх модулів, утиліт та можливостей платформи</p>
      </div>

      {HELP_SECTIONS.map(s => {
        if (s.divider) return (
          <div key={s.id} style={{ padding: "16px 0 6px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: t.textMuted }}>{s.divider}</div>
        );
        const isOpen = openId === s.id;
        return (
          <div key={s.id} style={{ ...card, padding: 0, overflow: "hidden", marginBottom: 8,
            border: isOpen ? `1px solid ${dark ? "rgba(99,102,241,0.3)" : "rgba(99,102,241,0.15)"}` : `1px solid ${t.border}`,
          }}>
            {/* Header */}
            <div onClick={() => setOpenId(isOpen ? null : s.id)} style={{
              display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", cursor: "pointer",
              background: isOpen ? (dark ? "rgba(99,102,241,0.06)" : "rgba(99,102,241,0.03)") : "transparent",
            }}>
              <span style={{ fontSize: 11, color: isOpen ? "#6366f1" : t.textMuted, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .2s" }}>▶</span>
              <span style={{ fontSize: 22 }}>{s.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{s.title}</div>
                <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2 }}>{s.desc}</div>
              </div>
            </div>

            {/* Details */}
            {isOpen && (
              <div style={{ padding: "0 18px 18px" }}>
                {/* What it does */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#6366f1", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Можливості</div>
                  {s.details.map((d, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "4px 0" }}>
                      <span style={{ color: "#6366f1", fontSize: 8, marginTop: 5 }}>●</span>
                      <span style={{ fontSize: 12, color: t.text }}>{d}</span>
                    </div>
                  ))}
                </div>

                {/* How to use */}
                <div style={{
                  background: dark ? "rgba(99,102,241,0.06)" : "rgba(99,102,241,0.03)",
                  borderRadius: 10, padding: "12px 14px",
                  border: `1px solid ${dark ? "rgba(99,102,241,0.15)" : "rgba(99,102,241,0.08)"}`,
                }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#6366f1", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Як користуватись</div>
                  <div style={{ fontSize: 12, color: t.text, lineHeight: 1.6 }}>{s.howTo}</div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Version info */}
      <div style={{ textAlign: "center", padding: "20px 0 10px", fontSize: 11, color: t.textMuted }}>
        Spalah 2.3 Beta — SEO + AEO Platform by svitlo.agency
      </div>
    </div>
  );
}
